import {
  Button,
  DetailBox,
  Loading,
  OutlinedSelect,
  SearchBox,
  Table,
  addToast,
} from '@octano/global-ui';
import { Card, Col, Row } from 'reactstrap';
import GoBackButton from '../../../../components/buttons/GoBackButton';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useCertificatesStudentsColumns } from '../hooks/useCertificatesStudentsColumns';
import { useCertificateStudents } from '../loaders/CertificateStudentsLoader';
import { NonStructuredCertificateStudentsFilters } from '../types';
import { useForm } from 'react-hook-form';
import { useCertificateDetailsInfo } from '../loaders/CertificateDetailsInfoLoader';
import { useTranslation } from 'react-i18next';
import { TranslationsKeys } from '../../../../locales/translations';
import DisplayError from '../../../../components/info/DisplayError';
import { useParams } from 'react-router-dom';
import { checkStudentsCompletion } from '../api';

export function CertificatesStudentsContent() {
  const { ceAcademicOfferId } = useParams<{ ceAcademicOfferId: string }>();
  const {
    data,
    loading,
    updateQuery,
    query,
    error: studentsError,
    refresh: refreshStudents,
  } = useCertificateStudents();
  const {
    data: detailsInfo,
    loading: detailsInfoLoading,
    error: detailsInfoError,
    refresh: refreshDetailsInfo,
  } = useCertificateDetailsInfo();

  const { t } = useTranslation(
    TranslationsKeys.CONTINUOUS_EDUCATION_CERTIFICATES,
  );

  const statusOptions = useMemo(
    () => [
      { label: t('students.filters.status.regular'), value: 'regular' },
      { label: t('students.filters.status.graduate'), value: 'egresado' },
      { label: t('students.filters.status.titled'), value: 'titulado' },
      { label: t('students.filters.status.failed'), value: 'reprobado' },
    ],
    [t],
  );

  const [updatingAll, setUpdatingAll] = useState(false);
  const [checkingId, setCheckingId] = useState<number | null>(null);

  const isGeneratingAny = useMemo(
    () => (data?.data ?? []).some((row) => row.certificateGenerating),
    [data],
  );

  const hadGeneratingRef = useRef(false);
  const loadingRef = useRef(loading);

  useEffect(() => {
    loadingRef.current = loading;
  }, [loading]);

  useEffect(() => {
    if (isGeneratingAny) {
      hadGeneratingRef.current = true;
      return;
    }

    if (hadGeneratingRef.current) {
      hadGeneratingRef.current = false;
      refreshStudents();
    }
  }, [isGeneratingAny, refreshStudents]);

  useEffect(() => {
    if (!isGeneratingAny || loading) {
      return;
    }

    const interval = setInterval(() => {
      if (loadingRef.current) {
        return;
      }
      refreshStudents();
    }, 10000);

    return () => clearInterval(interval);
  }, [isGeneratingAny, loading, refreshStudents]);

  const handleUpdateAll = useCallback(async () => {
    setUpdatingAll(true);
    try {
      const { data: result } = await checkStudentsCompletion(
        Number(ceAcademicOfferId),
      );
      if (result && result.updated > 0) {
        addToast({
          icon: 'success',
          color: 'success',
          text: t('students.updateSuccess', { count: result.updated }),
        });
      } else {
        addToast({
          icon: 'information',
          color: 'success',
          text: t('students.notUpdated'),
        });
      }
      refreshStudents();
    } catch {
      addToast({
        icon: 'error',
        color: 'danger',
        text: 'Error al actualizar estados',
      });
    } finally {
      setUpdatingAll(false);
    }
  }, [ceAcademicOfferId, refreshStudents, t]);

  const handleCheckSingle = useCallback(
    async (enrollmentId: number) => {
      setCheckingId(enrollmentId);
      try {
        const { data: result } = await checkStudentsCompletion(
          Number(ceAcademicOfferId),
          [enrollmentId],
        );
        if (result && result.updated > 0) {
          addToast({
            icon: 'success',
            color: 'success',
            text: t('students.updateSuccess', { count: result.updated }),
          });
        } else {
          addToast({
            icon: 'information',
            color: 'success',
            text: t('students.notUpdated'),
          });
        }
        refreshStudents();
      } catch {
        addToast({
          icon: 'error',
          color: 'danger',
          text: 'Error al actualizar estado',
        });
      } finally {
        setCheckingId(null);
      }
    },
    [ceAcademicOfferId, refreshStudents, t],
  );

  const columns = useCertificatesStudentsColumns({
    onCheckSingle: handleCheckSingle,
    checkingId,
    onAfterEnqueue: refreshStudents,
  });

  const {
    control,
    handleSubmit,
    setValue,
    reset,
  } = useForm<NonStructuredCertificateStudentsFilters>({
    defaultValues: {
      nameOrId: '',
      status: '',
    },
  });

  const handleSearch = (data: Record<string, any>) => {
    updateQuery({
      page: 1,
      items_per_page: 12,
      nameOrId: data.nameOrId,
      status: data.status?.value ?? undefined,
    });
  };

  const handleClean = () => {
    reset();
    updateQuery({
      page: 1,
      items_per_page: 12,
    });
  };

  const handleRetry = () => {
    if (detailsInfoError) {
      refreshDetailsInfo();
    } else {
      refreshStudents();
    }
  };

  const detailsColumns = useMemo(() => {
    return [
      {
        title: t('students.details.program'),
        body: detailsInfo?.program ?? '-',
      },
      {
        title: t('students.details.school'),
        body: detailsInfo?.school ?? '-',
      },
      {
        title: t('students.details.campus'),
        body: detailsInfo?.campus ?? '-',
      },
      {
        title: t('students.details.schedule'),
        body: detailsInfo?.schedule ?? '-',
      },
    ];
  }, [detailsInfo, t]);

  if (detailsInfoLoading) {
    return <Loading insideCard textBody="Cargando información de la oferta" />;
  }

  if (detailsInfoError || studentsError) {
    return (
      <DisplayError
        insideCard
        textBody={'Error al cargar la información'}
        retryAction={handleRetry}
        loadingAction={detailsInfoLoading || loading}
      />
    );
  }

  return (
    <Card className="p-4 mt-2 mx-3">
      <Col>
        <Row direction="row" justify="between">
          <Col xs={12} md={6}>
            <GoBackButton text="Volver" />
          </Col>
          <Col xs={12} md={6} className="py-3 py-lg-0">
            <DetailBox columns={detailsColumns} />
          </Col>
        </Row>
      </Col>
      <Col>
        <form onSubmit={handleSubmit(handleSearch)}>
          <Row direction="row" className="mt-4">
            <Col xs={12} style={{ paddingBottom: '20px' }}>
              <p className="fs-16">{t('students.subtitle')}</p>
            </Col>

            <Col xs={12} md={3}>
              <SearchBox
                name="nameOrId"
                label={t('students.filters.nameOrId.label')}
                placeholder={t('students.filters.nameOrId.placeholder')}
                control={control}
                clear={() => setValue('nameOrId', '')}
              />
            </Col>
            <Col xs={12} md={3}>
              <OutlinedSelect
                name="status"
                options={statusOptions}
                label={t('students.filters.status.label')}
                placeholder={t('students.filters.status.placeholder')}
                control={control}
                isClearable
              />
            </Col>
            <Col
              xs={12}
              md={6}
              style={{
                display: 'flex',
                flexDirection: 'column',
                justifyContent: 'flex-end',
              }}
            >
              <Row
                className="justify-content-end"
                style={{ paddingBottom: '14px', gap: '10px' }}
              >
                <Col xs={12} md={4} xl={3}>
                  <Button
                    text={t('students.filters.search')}
                    size="md"
                    fullwidth
                    type="submit"
                  />
                </Col>
                <Col xs={12} md={4} xl={3}>
                  <Button
                    text={t('students.filters.clear')}
                    size="md"
                    outlined
                    fullwidth
                    onClick={handleClean}
                  />
                </Col>
              </Row>
            </Col>
          </Row>
        </form>
      </Col>
      {loading ? (
        <Loading insideCard textBody="Cargando estudiantes" />
      ) : (
        <>
          <Col className="mt-5">
            <Row className="justify-content-between">
              <Col xs={12} md={6} className="d-flex align-items-center">
                <h2 className="fs-20 text-uppercase text-primary fw-700 mb-0 mt-2">
                  {t('students.table.title')}
                </h2>
              </Col>
              <Col xs={12} md={6} className="d-flex justify-content-end">
                <Button
                  text={t('students.table.updateStudentsStatusButton')}
                  size="md"
                  outlined
                  loading={updatingAll}
                  onClick={handleUpdateAll}
                />
              </Col>
            </Row>
          </Col>
          <Col className="mt-4">
            <Table
              columns={columns}
              data={data?.data ?? []}
              pagination={{
                itemsPerPage: 10,
                currentPage: query.page ?? 1,
                totalItems: data?.total ?? 0,
                totalPages: data?.total_pages ?? 0,
                onChangePage: (page) => updateQuery({ ...query, page }),
              }}
            />
          </Col>
        </>
      )}
    </Card>
  );
}
